import concat from 'lodash/concat'
import differenceInDays from 'date-fns/difference_in_calendar_days'
import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isAfterDate from 'date-fns/is_after'
import isDate from 'date-fns/is_date'
import isEqual from 'lodash/isEqual'
import isNumber from 'lodash/isNumber'
import isSameDay from 'date-fns/is_same_day'
import isString from 'lodash/isString'
import isValid from 'date-fns/is_valid'
import minBy from 'lodash/minBy'
import parseDate from 'date-fns/parse'
import reject from 'lodash/reject'
import some from 'lodash/some'
import values from 'lodash/values'
import {createSelector} from 'reselect'

import {hasLength} from './helpers'
import {formatDateString, isFilledString, or} from '../utils'
import {
  EventNumMembers,
  EventRoles,
  EventTypes,
  EventTypeNames,
  Madlibs,
  WeddingEventLookNames,
} from './enum'
import {
  DAY_CUTOFF_FOR_ADDING_PARTICIPANTS,
  EVENT_BOOKED,
  EVENT_IN_PROGRESS,
  EVENT_PAST,
  ApiRequestStates,
} from '../const'

// Get

export const all = (state) => get(state, 'events.all')
export const currentDate = (state) => get(state, 'events.current.date')
export const current = (state) => get(state, 'events.current')
export const currentId = (state) => get(state, 'events.current.id')
export const currentMetadata = (state) => get(state, 'events.current.metadata')
export const currentName = (state) => get(state, 'events.current.name')
export const currentNumMembers = (state) => get(state, 'events.current.numMembers')
export const currentOrderId = (state) => get(state, 'events.current.orderId')
export const currentOwnerId = (state) => get(state, 'events.current.owner')
export const currentOwnerRole = (state) => get(state, 'events.current.ownerRole')
export const currentType = (state) => get(state, 'events.current.type')
export const updatePartnerInfoRequestErrors = (state) =>
  get(state, 'events.updatePartnerInfoRequestErrors')
export const updatePartnerInfoRequestStatus = (state) =>
  get(state, 'events.updatePartnerInfoRequestStatus')

// Boolean

export const allEventsAreFetched = (state) => !!get(state, 'events.allEventsAreFetched')
export const currentEventIsChanging = (state) => !!get(state, 'events.currentEventIsChanging')
export const hasError = (state) => !!get(state, 'events.hasError')

// Helpers

export const dateIsValid = (date) => {
  if (!date) {
    return false
  }

  if (!isDate(date)) {
    return isValid(new Date(date))
  }

  return isValid(date)
}

export const getEventDate = (event) => get(event, 'date')
export const getEventId = (event) => get(event, 'id')
export const getEventMetadata = (event) => get(event, 'metadata')
export const getEventName = (event) => get(event, 'name')
export const getEventType = (event) => get(event, 'type')
export const getEventStatus = (event) => get(event, 'status')
export const getEventOwnerId = (event) => get(event, 'owner')
export const getEventHtoStatus = (event) => get(event, 'hasHto')
export const idIsValid = (id) => !!id
export const nameIsValid = isFilledString
export const typeIsValid = (val) => isNumber(val) && includes(values(EventTypes), val)
export const roleIsValid = (val) => isString(val) && includes(values(EventRoles), val)
export const numMembersIsValid = (val) => isNumber(val) && includes(EventNumMembers, val)

// Filtered User Events

// We remove the current event from "all" here as we want to pull it from "current" instead
// this allows any event updates to display automatically
export const allNonCurrentEvents = createSelector(all, currentId, (events, id) =>
  filter(events, (event) => event.id !== id),
)

export const bookedEvents = createSelector(all, ($all) => filter($all, {status: EVENT_BOOKED}))

export const inProgressEvents = createSelector(allNonCurrentEvents, ($allNonCurrentEvents) =>
  filter($allNonCurrentEvents, {status: EVENT_IN_PROGRESS}),
)

export const pastEvents = createSelector(all, ($all) => filter($all, {status: EVENT_PAST}))

export const paidEvents = createSelector(bookedEvents, pastEvents, concat)

// Args

export const eventById = (state, id) => find(all(state), {id})

export const isCurrentEvent = (state, id) => currentId(state) === id

export const eventDateById = (state, id) =>
  // if it's the current event, get the data from events.current, otherwise, get data from events.all
  // events.current data can be edited
  isCurrentEvent(state, id) ? currentDate(state) : getEventDate(eventById(state, id))

export const eventDaysAwayById = (state, id) =>
  differenceInDays(eventDateById(state, id), new Date())

export const eventIdById = (state, id) => id
// returns eventId to be used by other functions

export const eventMetadataById = (state, id) => getEventMetadata(eventById(state, id))

export const eventHasPartnerInfoById = (state, id) => !!eventMetadataById(state, id)?.partnerEmail

export const eventNameById = (state, id) =>
  isCurrentEvent(state, id) ? currentName(state) : getEventName(eventById(state, id))

export const eventStatusById = (state, id) => getEventStatus(eventById(state, id))

export const eventTypeNameById = (state, id) =>
  isCurrentEvent(state, id)
    ? EventTypeNames[currentType(state)]
    : EventTypeNames[getEventType(eventById(state, id))]

export const eventOwnerIdById = (state, id) =>
  isCurrentEvent(state, id) ? currentOwnerId(state) : getEventOwnerId(eventById(state, id))

export const eventHasHto = (state, id) => getEventHtoStatus(eventById(state, id))

// Computed

export const formattedEventDate = (format) =>
  createSelector(currentDate, (date) => formatDateString(date, format))

export const currentEventHasValidDate = createSelector(currentDate, dateIsValid)

export const currentEventHasValidName = createSelector(currentName, nameIsValid)

export const currentEventHasValidType = createSelector(currentType, typeIsValid)

export const currentEventHasValidRole = createSelector(currentOwnerRole, roleIsValid)

export const currentEventHasValidNumMembers = createSelector(currentNumMembers, numMembersIsValid)

export const currentEventRoleIsWeddingGuest = createSelector(
  currentType,
  currentOwnerRole,
  (type, role) => type === EventTypes.wedding && role === EventRoles.weddingGuest,
)

export const currentEventHasEventDetails = currentEventHasValidDate

export const currentEventIsInAll = createSelector(currentId, all, (id, $all) => some($all, {id}))

export const currentEventIsValid = createSelector(currentId, idIsValid)

export const currentEventTypeName = createSelector(currentType, (type) => EventTypeNames[type])

export const currentEventDaysAway = createSelector(currentDate, (currentEventDate) =>
  differenceInDays(currentEventDate, new Date()),
)

export const eventStatusIsBooked = createSelector(eventStatusById, (status) =>
  isEqual(status, EVENT_BOOKED),
)

export const canAddParticipants = createSelector(
  eventStatusIsBooked,
  eventDateById,
  (statusIsBooked, date) =>
    statusIsBooked &&
    differenceInDays(parseDate(date), new Date()) > DAY_CUTOFF_FOR_ADDING_PARTICIPANTS,
)

export const futureEvents = createSelector(all, ($all) =>
  filter($all, ({date}) => {
    const eventDate = parseDate(date)
    const today = new Date()
    return isSameDay(eventDate, today) || isAfterDate(eventDate, today)
  }),
)

export const hasBookedEvents = createSelector(bookedEvents, hasLength)

export const hasEventDetailsById = createSelector(eventDateById, eventTypeNameById, isFilledString)

export const hasEventsInAll = createSelector(all, hasLength)

export const hasEventsInAllOrCurrent = createSelector(
  // this returns true upon initial fetch and if customer deletes in progress events
  // and then creates a new event from top nav
  hasEventsInAll,
  currentEventIsValid,
  or,
)

export const hasInProgressEvents = createSelector(inProgressEvents, hasLength)

export const hasInProgressEventsOrCurrentEventIsValid = createSelector(
  hasInProgressEvents,
  currentEventIsValid,
  or,
)

export const hasPastEvents = createSelector(pastEvents, hasLength)

export const otherEvents = createSelector(current, all, (x, y) => reject(y, {id: getEventId(x)}))

export const hasOtherHto = createSelector(otherEvents, (events) =>
  events.some((event) => event.hasHto),
)

export const isMissingDetails = createSelector(
  currentEventHasValidDate,
  currentEventHasValidType,
  currentEventHasValidName,
  (x, y, z) => !x || !y || !z,
)

export const upcomingPaidEvents = createSelector(paidEvents, (events) =>
  filter(events, ({date}) => {
    const eventDate = parseDate(date)
    const today = new Date()
    return isSameDay(eventDate, today) || isAfterDate(eventDate, today)
  }),
)

export const soonestUpcomingPaidEvent = createSelector(upcomingPaidEvents, (events) =>
  minBy(events, ({date}) => new Date(date)),
)

export const soonestUpcomingPaidEventDate = createSelector(soonestUpcomingPaidEvent, getEventDate)

export const currentDateIsValue = (state, value) => value === currentDate(state)
export const currentTypeIsValue = (state, value) => value === currentType(state)
export const currentRoleIsValue = (state, value) => value === currentOwnerRole(state)
export const currentNumMembersIsValue = (state, value) => value === currentNumMembers(state)

// is a madlibs modal or dropdown option is equal to the corresponding value
//  held in store
export const madlibsOptionIsSelected = (state, id, value) => {
  switch (id) {
    case Madlibs.eventDate:
      return currentDateIsValue(state, value)
    case Madlibs.eventType:
      return currentTypeIsValue(state, value)
    case Madlibs.eventRole:
      return currentRoleIsValue(state, value)
    case Madlibs.numPeople:
      return currentNumMembersIsValue(state, value)
    default:
      return false
  }
}

export const currentTypeIsWedding = createSelector(
  currentType,
  (type) => type === EventTypes.wedding,
)

// if the event is not a wedding, user must submit valid event type and date
// if the event is a wedding, user must also submit a valid role and number of
//  members
export const madlibsIsCompleted = createSelector(
  currentTypeIsWedding,
  currentEventHasEventDetails,
  currentEventHasValidRole,
  currentEventHasValidNumMembers,
  (isWedding, hasDetails, hasRole, hasNumMembers) =>
    isWedding ? hasDetails && hasRole && hasNumMembers : hasDetails,
)

/**
 * Returns an event look name based on the event type and optional event role the user selects when adding product(s)
 * to a new look.
 *
 * @returns {String|undefined} Formatted event look name. Returns undefined if event type and/or event role does not
 *                             follow formatting rules.
 */
export const formatNewLookName = createSelector(
  currentType,
  currentOwnerRole,
  (eventType, eventRole) => {
    if (eventType === EventTypes.wedding) {
      switch (eventRole) {
        case EventRoles.bride:
        case EventRoles.groom:
          return WeddingEventLookNames.groom
        case EventRoles.groomsman:
          return WeddingEventLookNames.groomsman
        default:
          return WeddingEventLookNames.weddingGuest
      }
    }

    return EventTypeNames[eventType]
  },
)

export const isUpdatingPartnerInfo = createSelector(
  updatePartnerInfoRequestStatus,
  (status) => status === ApiRequestStates.PENDING,
)

export const hasUpdatePartnerInfoRequestFailed = createSelector(
  updatePartnerInfoRequestStatus,
  (status) => status === ApiRequestStates.ERROR,
)

export const currentEventHasPartnerInfo = createSelector(
  currentMetadata,
  (metadata) => !!metadata?.partnerEmail,
)
